import { useEffect, useRef, useLayoutEffect } from "react";
import { twMerge } from "tailwind-merge";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { ThemeType } from "@shared/types/style.types";
import { Type } from "@shared/ui/Type";
import { Button, Props as ButtonProps } from "@shared/ui/Button";
import { ActionButtonsGroup } from "@shared/ui/ActionButtonsGroup";

type DialogProps = {
  children?: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  containerClassName?: string;
  backdropClassName?: string;
  theme?: ThemeType;
  isWithActions?: boolean;
  isFixed?: boolean;
  onSecondaryClick?: (() => Promise<void>) | (() => void);
  onPrimaryClick?: (() => Promise<void>) | (() => void);
  secondaryButtonText?: string;
  primaryButtonText?: string;
  secondaryButtonProps?: ButtonProps;
  primaryButtonProps?: ButtonProps;
  title?: string;
  description?: string;
  size?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl";
};

export function Dialog({
  children,
  isOpen,
  onClose,
  theme,
  className,
  backdropClassName,
  containerClassName,
  isWithActions = true,
  isFixed = true,
  onSecondaryClick,
  onPrimaryClick,
  secondaryButtonText,
  primaryButtonText,
  secondaryButtonProps,
  primaryButtonProps,
  title,
  description,
  size = "lg",
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  // Store the previously focused element before the dialog is opened
  useLayoutEffect(() => {
    if (isOpen && previouslyFocusedElementRef.current == null) {
      previouslyFocusedElementRef.current =
        document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  const focusOnHeading = (dialogRef: React.RefObject<HTMLDivElement>) => {
    const activeEl = document.activeElement;
    const isInsideDialog = dialogRef.current?.contains(activeEl);

    if (!isInsideDialog) {
      const heading =
        dialogRef.current?.querySelector<HTMLElement>("#dialog-title");
      if (heading) heading.focus();
    }
  };

  // Focus on the first focusable element when the dialog is opened
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    focusOnHeading(dialogRef);
  }, [dialogRef, isOpen]);

  // Handle Enter key press to trigger primary button click
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement as HTMLElement | null;

      const tag = activeEl?.tagName?.toLowerCase();

      // Ignore if focus is inside an input, select, textarea, or button
      const isFormControl =
        ["input", "textarea", "select", "button"].includes(tag ?? "") ||
        activeEl?.isContentEditable ||
        activeEl?.getAttribute("role") === "textbox";

      if (e.key === "Enter" && !isFormControl) {
        e.preventDefault();
        onPrimaryClick?.();
      }
    };

    const el = dialogRef.current;
    el.addEventListener("keydown", handleKeyDown);

    return () => {
      el.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onPrimaryClick]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Disable body scroll when dialog is open
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;

    // Lock scroll
    document.body.style.overflow = "hidden";

    return () => {
      // Only restore if the dialog was the one to change it
      if (document.body.style.overflow === "hidden") {
        document.body.style.overflow = originalOverflow || "auto";
      }
    };
  }, [isOpen]);

  const fadeDuration = 0.05;
  const contentDuration = 0.05;

  const backdropVariants = {
    enter: {
      opacity: 1,
      transition: { duration: fadeDuration, delay: 0 },
    },
    exit: {
      opacity: 0,
      transition: { duration: fadeDuration, delay: contentDuration },
    },
  };

  const panelVariants = {
    enter: {
      opacity: 1,
      transition: {
        duration: contentDuration,
        delay: fadeDuration,
        type: "linear",
      },
    },
    exit: {
      opacity: 0,
      transition: { duration: contentDuration, delay: 0, type: "linear" },
    },
  };

  return (
    <AnimatePresence
      mode="wait"
      onExitComplete={() => {
        if (previouslyFocusedElementRef.current) {
          previouslyFocusedElementRef.current.focus();
          previouslyFocusedElementRef.current = null;
        }
      }}
    >
      {isOpen && (
        <motion.div
          tabIndex={-1}
          key="dialog-backdrop"
          variants={backdropVariants}
          initial="exit"
          animate="enter"
          exit="exit"
          className={twMerge(
            "inset-0 z-50 bg-brand-900/90 backdrop-blur-[4px] flex md:items-center md:justify-center",
            isFixed ? "fixed" : "absolute",
            backdropClassName
          )}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <motion.div
            key="dialog-panel"
            variants={panelVariants}
            ref={dialogRef}
            aria-labelledby="dialog-title"
            aria-modal="true"
            role="dialog"
            initial="exit"
            animate="enter"
            exit="exit"
            className={twMerge(
              "relative overflow-y-auto flex flex-col gap-4 bg-brand-50 min-h-dvh max-h-screen w-full md:rounded-lg md:min-h-auto ",
              size === "sm" && "max-w-sm",
              size === "md" && "max-w-md",
              size === "lg" && "max-w-lg",
              size === "xl" && "max-w-xl",
              size === "2xl" && "max-w-2xl",
              size === "3xl" && "max-w-3xl",
              size === "4xl" && "max-w-4xl",
              size === "5xl" && "max-w-5xl",
              size === "6xl" && "max-w-6xl",
              size === "7xl" && "max-w-7xl",
              containerClassName
            )}
          >
            <div className="flex flex-col gap-4 pt-6 pb-8 border-b border-brand-800">
              <div className="flex flex-row px-4 justify-between items-center md:items-start md:pl-8 md:pr-6">
                <Type
                  variant="h3"
                  tabIndex={-1}
                  id="dialog-title"
                  className="with-ring focus:rounded-md focus:ring-offset-2"
                >
                  {title}
                </Type>
                <div className="flex items-center justify-end">
                  <Button
                    variant="ghost"
                    iconComponent={X}
                    className="size-max"
                    onClick={onClose}
                    data-close-button
                    data-testid="dialog-close-button"
                    title="Close Dialog"
                    aria-label="Close Dialog"
                  />
                </div>
              </div>
              {description && (
                <div className="flex flex-row px-4 justify-between items-start md:px-8">
                  <Type variant="body2">{description}</Type>
                </div>
              )}
            </div>
            {children && (
              <div className="flex flex-1 flex-col justify-between">
                <div
                  className={twMerge("relative py-3 px-4 md:px-8", className)}
                >
                  {children}
                </div>
              </div>
            )}
            {isWithActions && (
              <ActionButtonsGroup
                className={twMerge(
                  "px-4 md:px-8 py-6 w-full bg-brand-50",
                  children && "border-t border-brand-800"
                )}
                isButtonsFullWidth={true}
                theme={theme}
                onSecondaryClick={onSecondaryClick}
                onPrimaryClick={onPrimaryClick}
                secondaryButtonText={secondaryButtonText}
                primaryButtonText={primaryButtonText}
                secondaryButtonProps={secondaryButtonProps}
                primaryButtonProps={primaryButtonProps}
              />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Dialog;
